/**
 * usePulseHighlight — shared primitive for ephemerally pulse-highlighting
 * specific DOM elements while a UI state is active.
 *
 * Originally two copies of this logic existed (GlossaryMode + DemoToolkit
 * tour). They shared: find-by-selector → add class → cleanup on state
 * change or unmount. This hook is the DRY version. Any new surface that
 * wants to say "hey look here" (e.g. the upcoming Hot-prospect CTA nudge
 * or a Demo-Director onboarding) can reuse it.
 *
 * Usage:
 *   usePulseHighlight({
 *     selectors: ['button[data-testid="save"]', '.primary-cta'],
 *     active: isOnboarding,
 *     className: 'onboarding-pulse-on',
 *   });
 *
 * Semantics:
 *   - No-op when `active` is falsy or `selectors` is empty.
 *   - rAF-delayed lookup so callers can flip `active` in the same render
 *     cycle as they mount the target elements (e.g. Tabs swap) without
 *     missing.
 *   - Cleans up on `active` change or unmount — zero leftover classes.
 *   - Idempotent — adding the class twice is harmless; we still remove
 *     it once on cleanup.
 *
 * The hook doesn't own the CSS — whoever calls it is responsible for
 * defining `.<className>` somewhere global (e.g. index.css).
 */
import { useEffect } from 'react';

export function usePulseHighlight({ selectors, active, className }) {
  useEffect(() => {
    if (!active || !className) return undefined;
    const selectorList = Array.isArray(selectors) ? selectors : [selectors];
    const cleaned = selectorList.filter((s) => typeof s === 'string' && s.length > 0);
    if (cleaned.length === 0) return undefined;

    let cancelled = false;
    const matched = new Set();

    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      cleaned.forEach((sel) => {
        let nodes;
        try {
          nodes = document.querySelectorAll(sel);
        } catch {
          // Invalid selector — skip silently. Don't let a typo in a
          // caller's config blow up their component tree.
          return;
        }
        nodes.forEach((n) => {
          n.classList.add(className);
          matched.add(n);
        });
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      matched.forEach((n) => n.classList.remove(className));
    };
  // `selectors` can be a string or array — we key on a stable string
  // representation so identical arrays across renders are treated as
  // the same dependency. Explicit expression is intentional.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, className, Array.isArray(selectors) ? selectors.join('|') : selectors]);
}
